import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewEncapsulation,
} from '@angular/core';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import {Router} from '@angular/router';
import {AppVersionService} from '@app/services/app-version.service';
import {CurrentPageBookmark, NavigationMenuService} from '@app/services/navigation-menu.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {MenuMode} from '@app/modules/shared/models/navigation';

/**
 * The top bar's own controls, in both layouts: Click to Update (New Version) when a newer
 * release is running, menu search, the star that bookmarks the page on screen, and the task
 * bell.
 */
@Component({
  selector: 'app-nav-toolbar',
  templateUrl: './nav-toolbar.component.html',
  styleUrls: ['./nav-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class NavToolbarComponent {
  @Input() showSearch = true;
  @Input() showTasks = true;

  readonly updateAvailable$ = this.appVersion.updateAvailable$;
  readonly currentPage$ = this.menu.currentPage$;
  readonly menuMode$ = this.navigationState.menuMode$;
  readonly menuAvailable$ = this.menu.available$;
  readonly isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '');

  confirmingUpdate = false;
  /** The bookmark popover: a name for a new bookmark, or a new name for this page's. */
  naming: {label: string; existing: boolean} | null = null;

  constructor(
    private appVersion: AppVersionService,
    private menu: NavigationMenuService,
    private navLayout: NavLayoutService,
    private navigationState: NavigationStateService,
    private announcer: LiveAnnouncer,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
  ) {
  }

  /**
   * Reload into the new release - after asking, when the page holds unsaved changes that a
   * reload would throw away.
   */
  update(): void {
    if (this.hasUnsavedWork() && !this.confirmingUpdate) {
      this.confirmingUpdate = true;
      return;
    }
    this.appVersion.reload();
  }

  cancelUpdate(): void {
    this.confirmingUpdate = false;
  }

  /** The menu layout switch, where it is always in sight. */
  setMenuMode(mode: MenuMode): void {
    this.navigationState.setMenuMode(mode);
    this.announcer.announce(mode === 'side' ? 'Side menu on' : 'Top menu on');
  }

  search(): void {
    this.navLayout.requestSearch();
  }

  /** The star opens the popover: bookmarking a page asks what to call it. */
  openBookmark(_shown: CurrentPageBookmark): void {
    if (this.naming) {
      this.naming = null;
      return;
    }
    // Asked afresh: a list page may have moved its filters into the address since the star rendered.
    const page = this.menu.currentPageNow();
    if (!page.kind) return;
    this.naming = {label: page.bookmarked ? page.currentLabel || page.label : page.label, existing: page.bookmarked};
    // select() alone highlights the name without moving focus, so typing and Esc went nowhere.
    setTimeout(() => {
      const input = this.host.nativeElement.querySelector<HTMLInputElement>('.sn-bookmark-popover input');
      input?.focus();
      input?.select();
    });
  }

  saveBookmark(label: string): void {
    if (!this.naming) return;
    const name = label.trim();
    if (this.naming.existing) {
      this.menu.renameCurrentPage(name);
      this.announcer.announce('Bookmark renamed');
    } else if (this.menu.bookmarkCurrentPage(name) === 'added') {
      this.announcer.announce(`Added ${name} to favorites`);
    }
    this.closeBookmark();
  }

  removeBookmark(): void {
    this.menu.removeCurrentPage();
    this.announcer.announce('Page removed from favorites');
    this.closeBookmark();
  }

  closeBookmark(): void {
    this.naming = null;
    this.host.nativeElement.querySelector<HTMLElement>('.sn-toolbar__star')?.focus();
  }

  get searchShortcut(): string {
    return this.isMac ? '⌘K' : 'Ctrl+K';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if ((this.confirmingUpdate || this.naming) && !this.host.nativeElement.contains(event.target as Node)) {
      this.confirmingUpdate = false;
      this.naming = null;
      this.cdr.markForCheck();
    }
  }

  /** Esc closes the popover wherever focus has gone (inside it, the form's own handler runs first). */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.naming) {
      this.closeBookmark();
      this.cdr.markForCheck();
    }
  }

  private hasUnsavedWork(): boolean {
    return typeof document !== 'undefined' && !!document.querySelector('form.ng-dirty');
  }
}
